<template>
  <section class="mt-12" id="technical-specifications">
    <div class="max-w-7xl mx-auto">
      <div class="text-center">
        <h2 class="text-xl md:text-3xl font-bold">
          <span class="text-orange-500">360sosyal vs. Diğerleri</span>
        </h2>
        <p class="text-slate-500 text-xs mt-2">
          Sosyal Medya Hesaplarınızı İster Aylık İster Anlık Hizmetlerle
          Dilediğiniz Gibi Özelleştirin
        </p>
      </div>
      <div class="bg-white rounded-md p-2 pb-0 pt-6 mt-8">
        <div class="grid grid-cols-3 gap-4">
          <div class="inline-flex items-center">
            <span class="text-lg font-bold ml-4">Özellik</span>
          </div>
          <div class="inline-flex justify-center">
            <div
              class="w-40 bg-orange-500 rounded text-lg text-white text-center p-2"
            >
              360sosyal
            </div>
          </div>
          <div class="inline-flex justify-center">
            <div
              class="w-40 landing-light-orange rounded text-lg text-orange-500 text-center p-2"
            >
              Diğerleri
            </div>
          </div>
          <div class="h-1 col-span-3 bg-orange-500"></div>
        </div>

        <template v-for="(item, index) in tableItems" :key="index">
          <div
            class="grid grid-cols-3 items-center gap-4 py-2 pl-1 md:pl-0"
            :class="`${index % 2 == 0 ? 'landing-light-orange' : ''}`"
          >
            <div>
              <span
                class="text-xs md:text-sm text-slate-600 ml-0 md:ml-4"
                :aria-label="item.feature"
              >
                {{ item.feature }}
              </span>
            </div>
            <div class="flex items-center justify-center">
              <template v-if="typeof item.social360 === 'boolean'">
                <div
                  class="size-5 rounded-full bg-orange-500 flex items-center justify-center"
                >
                  <Icon name="material-symbols:check" class="text-white" />
                </div>
              </template>
              <span
                class="text-xs md:text-sm text-orange-500 text-center"
                v-else
                >{{ item.social360 }}</span
              >
            </div>
            <div class="inline-flex justify-center">
              <template v-if="typeof item.others === 'boolean'">
                <div
                  class="size-5 rounded-full bg-black flex items-center justify-center"
                >
                  <Icon name="iconoir:xmark" class="text-white" />
                </div>
              </template>
              <span
                class="text-xs md:text-sm text-slate-800 text-center"
                v-else
                >{{ item.others }}</span
              >
            </div>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
interface TableItem {
  feature: string
  social360: boolean | string
  others: boolean | string
}

const tableItems = ref<TableItem[]>([
  {
    feature: 'Gerçek Kullanıcılarla Etkileşim',
    social360: true,
    others: false,
  },
  {
    feature: 'Organik Hizmetler',
    social360: true,
    others: false,
  },
  {
    feature: 'Otomatik Etkileşim Sistemi',
    social360: true,
    others: false,
  },
  {
    feature: 'Öne Çıkarma Desteği',
    social360: true,
    others: false,
  },
  {
    feature: 'Cinsiyet Seçimi',
    social360: true,
    others: false,
  },
  {
    feature: 'Özelleştirilebilir Siparişler',
    social360: true,
    others: false,
  },
  {
    feature: 'Kesintisiz Etkileşim',
    social360: true,
    others: false,
  },
  {
    feature: 'Kademeli & Dengeli Büyüme',
    social360: true,
    others: false,
  },
  {
    feature: 'Anlık Başlangıç & Hızlı İşlem Süresi',
    social360: true,
    others: 'Gecikmeli İşlemler',
  },
  {
    feature: 'Platform Uyumluluğu – Çoklu Platform Desteği',
    social360: true,
    others: 'Sınırlı Seçenek',
  },
  {
    feature: 'Piyasada Bulunmayan Spesifik Platformlar',
    social360: true,
    others: 'Sadece Standart Platformlar',
  },
  {
    feature: 'Şeffaflık',
    social360: 'Özel Kanıt Sistemli',
    others: false,
  },
  {
    feature: 'Güvenilirlik & Hesap Güvenliği',
    social360: 'Hesabınız Koruma Altında',
    others: false,
  },
  {
    feature: 'Esnek Kullanım (Abonelik / Anlık Sipariş)',
    social360: 'Size Uygun Planlama',
    others: false,
  },
  {
    feature: 'Bot Hizmetler',
    social360: 'İsteğe Bağlı',
    others: true,
  },
  {
    feature: 'Fiyatlandırma',
    social360: 'Kendiniz Belirleyin',
    others: 'Sabit Fiyat',
  },
  {
    feature: 'Müşteri Destek Hızı',
    social360: '7/24 Destek',
    others: 'Yanıt Süresi Uzun',
  },
  {
    feature: 'Müşteriye Özel Servis Oluşturma',
    social360: 'Talebe Göre Yeni Hizmet',
    others: 'Sabit Servisler',
  },
  {
    feature: 'Kullanıcı Deneyimi & Kolay Kullanım',
    social360: 'Kullanıcı Dostu Arayüz',
    others: 'Karmaşık Paneller',
  },
  {
    feature: 'Düşük Düşme Oranı',
    social360: 'Kalıcı Etkileşimler',
    others: 'Ani Düşüşler Olabilir',
  },
  {
    feature: 'Özel Kampanya & İndirimler',
    social360: 'Esnek Ödeme Seçenekleri',
    others: 'Sabit Fiyatlandırma',
  },
  {
    feature: 'Doğal Görünümlü Etkileşimler',
    social360: 'Gerçekçi Etkileşim Akışı',
    others: 'Sistematik & Yapay Görünüm',
  },
])
</script>

<style scoped>
.landing-light-orange {
  background-color: #f4e7de;
}
.grid-cols-3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.col-span-3 {
  grid-column: span 3 / span 3;
}
.bg-black {
  background-color: #000;
}
</style>
